import React from 'react';
import { PropTypes } from 'prop-types';
// styles
import styled from 'styled-components';
// state
import { useSoFormStepsApi, useSoFormStepsState } from '../../SoForm.context';
// components
import SoFormButton from '../../components/other/SoFormButton';
import SoFormContainer from '../../components/layout/SoFormContainer';
import { CheckCircleIcon } from 'refactored/ui/icons';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';

const MainContainer = styled.div`
  /* Frame 188 */

  /* Auto layout */

  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 48px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 16px;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;

  width: 544px;
  height: 151px;
`;

const HeadingTextBig = styled.div`
  height: 40px;

  /* 32px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 32px;
  line-height: 40px;
  /* identical to box height, or 125% */

  text-align: center;

  /* primary/500 */

  color: #0140cd;
`;
const HeadingTextRegular = styled.div`
  height: 32px;

  /* 14px/regular */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  /* or 114% */

  text-align: center;

  /* grey / 700 */

  color: #54708c;
  display: flex;
  align-items: center;
`;

const IdContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;

  height: 36px;

  /* grey / 200 */

  background: #e2e8ee;
  border-radius: 4px;
`;

const IdText = styled.div`
  height: 16px;

  /* 14px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  /* identical to box height, or 114% */

  /* grey / 700 */

  color: #54708c;
`;

const StopsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 16px;
`;
const StopsInnerContainer = styled.div`
  /* Auto layout */

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px 32px 24px;
  gap: 24px;

  /* Flowkit/White */

  background: #ffffff;
  border-radius: 4px;
`;

const StopsItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
`;

const StopInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
`;

const StopTitle = styled.div`
  width: 480px;
  height: 24px;

  /* 20px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  /* identical to box height, or 120% */

  display: flex;
  align-items: center;

  /* grey / 800 */

  color: #3b4f63;
`;

const StopDate = styled.div`
  width: 480px;
  height: 24px;

  /* 16px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  display: flex;
  align-items: center;

  /* grey / 500 */

  color: #94a6b8;
`;

const StopSeparator = styled.div`
  height: 0px;

  /* grey / 200 */

  border: 1px solid #e2e8ee;
`;

const getFormattedDateFromDate = (date) => {
  return moment(date).format('dddd D MMMM');
};

const getFormattedRangeFromDates = (dateFrom, dateTo) => {
  return `${moment(dateFrom).format('HH:mm')} - ${moment(dateTo).format(
    'HH:mm'
  )}`;
};

const Stops = () => {
  const { t } = useTranslation('Catalog');
  const stepsState = useSoFormStepsState();
  const quotePayload = stepsState.activeQuotePayload;
  const pickupTittle = quotePayload.shipments[0].address_from.city;
  const dropOffTittle = quotePayload.shipments[0].address_to.city;
  const shipment = quotePayload.shipments[0];

  const pickupDate = `${getFormattedDateFromDate(
    shipment.pickup_time_from
  )}, ${t('success.pickup')} ${getFormattedRangeFromDates(
    shipment.pickup_time_from,
    shipment.pickup_time_to
  )}`;
  const dropoffDate = `${getFormattedDateFromDate(
    shipment.dropoff_time_from
  )}, ${t('success.dropoff')} ${getFormattedRangeFromDates(
    shipment.dropoff_time_from,
    shipment.dropoff_time_to
  )}`;

  return (
    <StopsContainer>
      <StopsInnerContainer>
        <StopsItemsContainer>
          <Details>
            <StopInfo>
              <StopTitle>
                {t('confirmation.pickup_in')} {pickupTittle}
              </StopTitle>
              <StopDate>{pickupDate}</StopDate>
            </StopInfo>
            <StopSeparator />
            <StopInfo>
              <StopTitle>
                {t('confirmation.dropoff_in')}
                {dropOffTittle}
              </StopTitle>
              <StopDate>{dropoffDate}</StopDate>
            </StopInfo>
          </Details>
        </StopsItemsContainer>
      </StopsInnerContainer>
    </StopsContainer>
  );
};

const Heading = () => {
  const { t } = useTranslation('Catalog');
  const stepsState = useSoFormStepsState();
  const selectedProductType = stepsState.price.selectedProductType;
  const candidates = stepsState.price.candidates;
  const selectedCandidate = candidates.find(
    (c) => c.productType === selectedProductType
  );
  const isRFQ = selectedCandidate.pricing_strategy !== 'INSTANT';
  return (
    <HeadingContainer>
      <CheckCircleIcon />
      <HeadingTextBig>{t('success.order_created')}</HeadingTextBig>
      <HeadingTextRegular>
        {!isRFQ && <>{t('success.non_rfq_detail')}</>}
        {isRFQ && <>{t('success.rfq_detail')}</>}
      </HeadingTextRegular>
    </HeadingContainer>
  );
};

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 8px;
  ${SoFormButton} {
    width: unset;
  }
`;

const Actions = () => {
  const { t } = useTranslation('Catalog');

  const stepsApi = useSoFormStepsApi();
  return (
    <ActionsContainer>
      <SoFormButton inverse={true} onClick={stepsApi.onSuccessGoToShipments}>
        {t('success.go_to_orders')}
      </SoFormButton>
      <SoFormButton onClick={stepsApi.onSuccessNewOrder}>
        {t('success.new_order')}
      </SoFormButton>
    </ActionsContainer>
  );
};

const SoFormStepSuccessBody = ({ className }) => {
  const stepsState = useSoFormStepsState();
  const id = stepsState.success.id;
  const { t } = useTranslation('Catalog');

  return (
    <div className={className}>
      <SoFormContainer>
        <MainContainer>
          <InfoContainer>
            <Heading />
            <IdContainer>
              <IdText>
                {t('success.shipment_id')}: S{id}
              </IdText>
            </IdContainer>
            <Stops />
          </InfoContainer>
          <Actions />
        </MainContainer>
      </SoFormContainer>
    </div>
  );
};

SoFormStepSuccessBody.propTypes = {
  className: PropTypes.string,
};

export default styled(SoFormStepSuccessBody)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 0px;
  width: 100%;
  ${SoFormContainer} {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0px;
    gap: 16px;
    padding: 0;
  }
  flex-grow: 1;
`;
